import { useContext, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenuItem,
  Button,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent
} from '@@/ui/primitives';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { UploadIcon, RefreshCwIcon, FileIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Spinner } from '@@/ui/components/Spinner';
import { AuthContext } from '@@/contexts/AuthContext';
import { DfcApiClient } from '../../../../../api/dfc-api-client';
import { ResultValue } from '@@/types/ApiResult';
import { BulkImportStartResponse, ImportError } from '../../../../../api/api-tools';
import { useQueryClient } from '@tanstack/react-query';
import { itemsQueryKeys } from '../../../../../hooks/use-items';
import { entitiesQueryKeys } from '../../../../../hooks/use-entities';

enum ImportStatus {
  IDLE = 'IDLE',
  UPLOADING = 'UPLOADING',
  IMPORTING = 'IMPORTING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ImportErrorItem(props: { error: ImportError }) {
  const { error } = props;
  return (
    <div className='border-b border-border pb-2 last:border-b-0 space-y-1'>
      <div className='font-medium text-red-950'>{error.reason}</div>
      <div className='text-red-800 text-xs'>
        Entity: {error.entityId}
        {error.itemId && ` | Item: ${error.itemId}`}
        {error.fieldName && ` | Field: ${error.fieldName}`}
      </div>
    </div>
  );
}

export function EntityImportDialog(props: { baseEntityId: string }) {
  const [status, setStatus] = useState<ImportStatus>(ImportStatus.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<BulkImportStartResponse | null>(null);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!instanceData?.instanceId) {
      setError('Missing required context data');
      setStatus(ImportStatus.ERROR);
      return;
    }

    if (!selectedFile) {
      setError('Please select a file to import');
      setStatus(ImportStatus.ERROR);
      return;
    }

    setStatus(ImportStatus.UPLOADING);
    setError(null);

    try {
      const dfcApiClient = new DfcApiClient();

      const presignedResult = await dfcApiClient.tools.bulkImport({
        input: {
          action: 'get_presigned_url',
          baseEntityId: props.baseEntityId,
          contentLength: selectedFile.size
        },
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(presignedResult)) {
        throw new Error(presignedResult.message ?? 'Failed to get upload URL');
      }

      const presignedData = presignedResult.data;
      if (!('url' in presignedData) || !('objectKey' in presignedData)) {
        throw new Error('Invalid presigned URL response');
      }

      const { url, objectKey } = presignedData;

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      setStatus(ImportStatus.IMPORTING);

      const importResponse = await dfcApiClient.tools.bulkImport({
        input: {
          action: 'start_import',
          baseEntityId: props.baseEntityId,
          objectKey
        },
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(importResponse)) {
        throw new Error(importResponse.message ?? 'Failed to start import');
      }

      setImportResult(importResponse.data as BulkImportStartResponse);
      setStatus(ImportStatus.SUCCESS);

      if (instanceData?.instanceId && instanceData?.region) {
        queryClient.invalidateQueries({
          queryKey: entitiesQueryKeys.base({
            instanceId: instanceData.instanceId,
            region: instanceData.region
          })
        });
        queryClient.invalidateQueries({
          queryKey: itemsQueryKeys.base({
            instanceId: instanceData.instanceId,
            region: instanceData.region
          })
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setStatus(ImportStatus.ERROR);
    }
  };

  const handleReset = () => {
    setStatus(ImportStatus.IDLE);
    setSelectedFile(null);
    setError(null);
    setImportResult(null);
    setErrorsExpanded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isLoading = status === ImportStatus.UPLOADING || status === ImportStatus.IMPORTING;

  return (
    <Dialog>
      <DialogTrigger>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <UploadIcon />
          Import
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className='font-sans font-[450]'>
        <DialogHeader>
          <DialogTitle>Import</DialogTitle>
          <DialogDescription>
            Upload a{' '}
            <a href='https://jsonlines.org/' target='_blank' className='text-blue'>
              JSON Lines
            </a>{' '}
            file to import Entities and Items into this Entity.
          </DialogDescription>
        </DialogHeader>

        {status === ImportStatus.IDLE && (
          <div className='space-y-4'>
            <input
              ref={fileInputRef}
              type='file'
              accept='.jsonl,.json'
              onChange={handleFileSelect}
              className='hidden'
            />
            {selectedFile ? (
              <div
                className='flex items-center justify-between
                  bg-muted-foreground/10 p-3 rounded'
              >
                <div className='flex items-center gap-2'>
                  <FileIcon className='h-4 w-4' />
                  <span className='text-sm font-medium truncate max-w-60'>{selectedFile.name}</span>
                  <span className='text-sm text-muted-foreground text-nowrap'>{formatFileSize(selectedFile.size)}</span>
                </div>
                <Button variant='ghost' size='sm' onClick={handleRemoveFile}>
                  <XIcon className='h-4 w-4' />
                </Button>
              </div>
            ) : (
              <Button variant='outline' onClick={() => fileInputRef.current?.click()} className='w-full'>
                <UploadIcon className='mr-2 h-4 w-4' />
                Select file
              </Button>
            )}
            {selectedFile && (
              <p className='text-sm text-muted-foreground'>
                Click "Start import" to begin importing the selected file. This may take some time depending on the file
                size.
              </p>
            )}
          </div>
        )}

        {status === ImportStatus.UPLOADING && (
          <div className='flex items-center gap-3'>
            <Spinner size='sm' />
            <span className='text-sm'>Uploading file...</span>
          </div>
        )}

        {status === ImportStatus.IMPORTING && (
          <div className='flex items-center gap-3'>
            <Spinner size='sm' />
            <span className='text-sm'>Processing import...</span>
          </div>
        )}

        {status === ImportStatus.SUCCESS && importResult && (
          <div className='text-sm space-y-4'>
            <p className='text-muted-foreground font-medium'>Your import has been processed.</p>
            <div className='grid grid-cols-3 gap-2'>
              <div
                className='bg-muted/50 rounded p-3 text-center
                  flex flex-col'
              >
                <span className='text-xl font-semibold'>{importResult.lines}</span>
                <span className='text-xs text-muted-foreground'>Lines processed</span>
              </div>
              <div
                className='bg-muted/50 rounded p-3 text-center
                  flex flex-col'
              >
                <span className='text-xl font-semibold'>{importResult.importedEntityCount}</span>
                <span className='text-xs text-muted-foreground'>Entities imported</span>
              </div>
              <div
                className='bg-muted/50 rounded p-3 text-center
                  flex flex-col'
              >
                <span className='text-xl font-semibold'>{importResult.importedItemCount}</span>
                <span className='text-xs text-muted-foreground'>Items imported</span>
              </div>
            </div>

            {importResult.errorCount > 0 && (
              <Collapsible
                open={errorsExpanded}
                onOpenChange={setErrorsExpanded}
                className='border rounded-md p-2 bg-red-50 border-red-100'
              >
                <CollapsibleTrigger className='flex items-center gap-2 w-full text-red-950 text-sm'>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform
                      ${errorsExpanded ? 'rotate-180' : ''}`}
                  />
                  <span className='font-medium'>
                    {importResult.errorCount} error{importResult.errorCount > 1 ? 's' : ''}
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className='mt-2'>
                  <div className='max-h-40 overflow-y-auto space-y-2 text-xs'>
                    {importResult.errors.map((err, idx) => (
                      <ImportErrorItem key={idx} error={err} />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            <div className='flex justify-center'>
              <Button variant='outline' size='default' onClick={handleReset}>
                <RefreshCwIcon />
                Import another
              </Button>
            </div>
          </div>
        )}

        {status === ImportStatus.ERROR && error && <div className='text-sm text-destructive'>{error}</div>}

        <DialogFooter>
          {status !== ImportStatus.SUCCESS && (
            <Button onClick={handleImport} disabled={isLoading || !selectedFile}>
              {isLoading ? (
                <>
                  <Spinner size='sm' className='mr-2' />
                  {status === ImportStatus.UPLOADING ? 'Uploading...' : 'Importing...'}
                </>
              ) : (
                'Start import'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
